import type { EventRoleKind, RideItem, RoleWeekdayInput, RolesInput } from '@amumin/api-client';
import { t, type AppMessages } from '@amumin/i18n';
import { compactWeekdayChoices, rideWeekdays } from '@amumin/domain/recurrence';

import { repeatToInput, type RepeatForm } from './recurrenceForm.js';

// Brengen en halen in de afspraak-editor (0022, DESIGN.md §8.W3): eenvoudig (één keuze voor
// brengen en één voor halen) of "Per dag anders" (per weekdag van de herhaling). Het formulier
// houdt per dag een keuze bij; bij opslaan wordt dat de standaard van de serie (de meest gekozen
// rijder) plus een regel voor elke dag die afwijkt, met dezelfde functie als de server
// (compactWeekdayChoices). Getest in rides.test.ts.

/** Keuze in het formulier: lid-id, 'open' (nog niet bekend) of 'none' (niet nodig). */
export type RoleChoice = 'none' | 'open' | string;

export interface DayChoice {
  dropoff: RoleChoice;
  pickup: RoleChoice;
}

/** Keuze per ISO-weekdag (1 = maandag ... 7 = zondag). */
export type RideDays = Record<number, DayChoice>;

export interface RidesFormPart {
  dropoff: RoleChoice;
  pickup: RoleChoice;
  dropoffOffset: number;
  pickupOffset: number;
  /** null = eenvoudig (elke keer dezelfde). */
  rideDays: RideDays | null;
  repeat: RepeatForm;
  date: string;
}

const ROLES: readonly EventRoleKind[] = ['dropoff', 'pickup'];

export function roleChoiceOf(role: { member_id: string | null } | null | undefined): RoleChoice {
  if (!role) return 'none';
  return role.member_id ?? 'open';
}

/** Weekdagen van de herhaling in het formulier waarop per dag iemand anders kan rijden. */
export function formWeekdays(form: Pick<RidesFormPart, 'repeat' | 'date'>): number[] {
  const rec = repeatToInput(form.repeat, form.date);
  return rideWeekdays(rec ? rec.rrule : null, form.date);
}

/** Standaardrijders en regels per weekdag: uit het detail van een serie of uit een sjabloon. */
export interface RideSourceData {
  roles: {
    dropoff?: { member_id: string | null } | null;
    pickup?: { member_id: string | null } | null;
  };
  role_weekdays?:
    | ReadonlyArray<{
        role: EventRoleKind;
        weekday: number;
        member_id: string | null;
        needed?: boolean;
      }>
    | undefined;
}

/** Per-dag-keuzes uit het detail of sjabloon (alleen als er regels per weekdag zijn). */
export function daysFromDetail(d: RideSourceData, weekdays: readonly number[]): RideDays | null {
  const rules = d.role_weekdays ?? [];
  if (rules.length === 0) return null;
  const out: RideDays = {};
  for (const wd of weekdays) {
    const day: DayChoice = {
      dropoff: roleChoiceOf(d.roles.dropoff),
      pickup: roleChoiceOf(d.roles.pickup),
    };
    for (const role of ROLES) {
      const rule = rules.find((r) => r.role === role && r.weekday === wd);
      // needed ontbreekt in een sjabloon = nodig (zoals de API).
      if (rule) day[role] = rule.needed === false ? 'none' : (rule.member_id ?? 'open');
    }
    out[wd] = day;
  }
  return out;
}

/** Per dag beginnen met de eenvoudige keuze (knop "Per dag anders"). */
export function startDays(form: RidesFormPart, weekdays: readonly number[]): RideDays {
  const out: RideDays = {};
  for (const wd of weekdays) {
    out[wd] = form.rideDays?.[wd] ?? { dropoff: form.dropoff, pickup: form.pickup };
  }
  return out;
}

/** Terug naar eenvoudig: per rol de meest gekozen rijder. */
export function collapseDays(
  form: RidesFormPart,
  weekdays: readonly number[],
): { dropoff: RoleChoice; pickup: RoleChoice } {
  const pick = (role: EventRoleKind): RoleChoice => {
    const choices = weekdays.map((wd) => ({
      weekday: wd,
      choice: form.rideDays?.[wd]?.[role] ?? form[role],
    }));
    const c = compactWeekdayChoices(role, choices);
    if (!c.default) return 'none';
    return c.default.memberId ?? 'open';
  };
  return { dropoff: pick('dropoff'), pickup: pick('pickup') };
}

/**
 * Kan "Per dag anders" niet meer (minder dan twee weekdagen, bv. donderdag uit de herhaling
 * gehaald), dan wordt de verdeling per dag de eenvoudige keuze: bij één overgebleven dag de keuze
 * van die dag, anders per rol de meest gekozen rijder. Zo toont de editor precies wat er wordt
 * opgeslagen (review c-fs r1: de tabel verdween en een oude keuze werd stil bewaard).
 */
export function settleRideDays<T extends RidesFormPart>(form: T): T {
  if (!form.rideDays) return form;
  const weekdays = formWeekdays(form);
  if (weekdays.length >= 2) return form;
  const basis =
    weekdays.length === 1
      ? weekdays
      : Object.keys(form.rideDays)
          .map(Number)
          .sort((a, b) => a - b);
  return { ...form, ...collapseDays(form, basis), rideDays: null };
}

/**
 * Formulier → `roles` en `role_weekdays` voor de API. `role_weekdays` ontbreekt als per dag hier
 * niet kan (eenmalig, maandelijks, jaarlijks); anders is het de volledige lijst (leeg = elke keer
 * dezelfde).
 */
export function ridesInput(form: RidesFormPart): {
  roles: RolesInput;
  role_weekdays?: RoleWeekdayInput[];
} {
  const weekdays = formWeekdays(form);
  const one = (choice: RoleChoice, offset: number) =>
    choice === 'none'
      ? null
      : { member_id: choice === 'open' ? null : choice, offset_minutes: offset };
  if (!form.rideDays || weekdays.length < 2) {
    // Zonder tabel geldt de eenvoudige keuze die op het scherm staat (de editor zet een
    // overgebleven dag daar eerst in met settleRideDays).
    const roles = {
      dropoff: one(form.dropoff, form.dropoffOffset),
      pickup: one(form.pickup, form.pickupOffset),
    };
    return weekdays.length > 0 ? { roles, role_weekdays: [] } : { roles };
  }
  const roles: RolesInput = {};
  const rules: RoleWeekdayInput[] = [];
  for (const role of ROLES) {
    const choices = weekdays.map((wd) => ({
      weekday: wd,
      choice: form.rideDays?.[wd]?.[role] ?? form[role],
    }));
    const c = compactWeekdayChoices(role, choices);
    const offset = role === 'dropoff' ? form.dropoffOffset : form.pickupOffset;
    roles[role] = c.default ? { member_id: c.default.memberId, offset_minutes: offset } : null;
    for (const r of c.rules) {
      rules.push({ role, weekday: r.weekday, member_id: r.memberId, needed: r.needed });
    }
  }
  return { roles, role_weekdays: rules };
}

/** Wijkt de verdeling per dag af van het begin? (dan kan "alleen deze keer" niet.) */
export function rideDaysChanged(form: RidesFormPart, initial: RidesFormPart): boolean {
  return (
    JSON.stringify(ridesInput(form).role_weekdays ?? []) !==
    JSON.stringify(ridesInput(initial).role_weekdays ?? [])
  );
}

/**
 * Dagen met een eigen rijder die uit de herhaling verdwijnen (de editor zegt vooraf dat die
 * rijder vervalt; de server snoeit ze ook).
 */
export function droppedRideDays(
  initial: RidesFormPart | null,
  weekdays: readonly number[],
): number[] {
  if (!initial || !initial.rideDays) return [];
  const had = ridesInput(initial).role_weekdays ?? [];
  const days = [...new Set(had.map((r) => r.weekday))];
  return days.filter((d) => weekdays.indexOf(d) < 0).sort((a, b) => a - b);
}

/**
 * Vraag bij een rit zonder rijder in de taal van de app (oprichter 1 okt; niet de Nederlandse
 * label_nl van de server): "Wie brengt Sem naar Zwemles?". names = wie meegaat, al als lijst.
 */
export function openRideLabel(
  ride: Pick<RideItem, 'role' | 'title'>,
  names: string,
  w: AppMessages['omgeving']['rideOpen'],
): string {
  const tpl =
    ride.role === 'dropoff'
      ? names
        ? w.dropoff
        : w.dropoffNoNames
      : names
        ? w.pickup
        : w.pickupNoNames;
  return t(tpl, { names, title: ride.title });
}
